/**
 * cm-gallery-dots.js
 *
 * Haelt die Punkt-Reihe unter der Produktgalerie auf konstanter Breite.
 *
 * Ohne das hier waechst die Reihe bei vielen Bildern immer weiter und der
 * aktive Punkt wandert nach aussen. Stattdessen bleibt ein festes Fenster von
 * 10 Punkten stehen; der Streifen schiebt sich darin, sodass der aktive Punkt
 * mittig bleibt. An den Enden wird der Schub begrenzt, damit der erste bzw.
 * letzte Punkt buendig sitzt.
 *
 * Die Zentrierung selbst macht CSS (width: fit-content + margin-inline: auto).
 * Dieses Skript setzt nur --dot-shift und markiert die Punkte am Fensterrand,
 * damit sie kleiner auslaufen statt hart abgeschnitten zu werden. Faellt das
 * Skript aus, sitzt die Reihe trotzdem mittig.
 *
 * Die eigentliche Auswahl macht weiterhin Horizons Slideshow-Komponente.
 */

const CM_DOT_WINDOW = 10;
const CM_DOTS_SELECTOR = 'media-gallery .slideshow-controls__dots';

function cmUpdateDots(dots) {
  const items = Array.from(dots.querySelectorAll(':scope > li'));
  if (!items.length) return;

  const total = items.length;
  const windowSize = Math.min(CM_DOT_WINDOW, total);

  dots.style.setProperty('--dot-window', String(windowSize));

  const index = items.findIndex((item) => item.querySelector('button')?.getAttribute('aria-selected') === 'true');

  let shift = 0;
  if (total > CM_DOT_WINDOW && index >= 0) {
    shift = index - Math.floor(CM_DOT_WINDOW / 2);
    shift = Math.max(0, Math.min(shift, total - CM_DOT_WINDOW));
  }

  dots.style.setProperty('--dot-shift', String(shift));

  // Randabstufung: der aeusserste sichtbare Punkt wird am kleinsten, der
  // naechste etwas groesser. Nur auf der Seite, auf der es noch weitergeht.
  const first = shift;
  const last = shift + windowSize - 1;

  for (const [position, item] of items.entries()) {
    let edge = null;

    if (shift > 0) {
      if (position === first) edge = 'outer';
      else if (position === first + 1) edge = 'inner';
    }

    if (last < total - 1) {
      if (position === last) edge = 'outer';
      else if (position === last - 1) edge = 'inner';
    }

    if (edge) item.setAttribute('data-cm-edge', edge);
    else item.removeAttribute('data-cm-edge');
  }
}

function cmBindDots(dots) {
  if (dots.cmDotsBound) return;
  dots.cmDotsBound = true;

  const update = () => cmUpdateDots(dots);

  // Horizon setzt aria-selected um; darauf reagieren wir, statt eigene
  // Scroll-Logik danebenzustellen. childList faengt Galerien ab, die nach
  // einem Variantenwechsel mit anderer Bildanzahl neu gerendert werden.
  new MutationObserver(update).observe(dots, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['aria-selected'],
  });

  update();
}

function cmInitDots(root = document) {
  if (!root?.querySelectorAll) return;

  if (root instanceof Element && root.matches?.(CM_DOTS_SELECTOR)) cmBindDots(root);
  root.querySelectorAll(CM_DOTS_SELECTOR).forEach(cmBindDots);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => cmInitDots());
} else {
  cmInitDots();
}

document.addEventListener('shopify:section:load', (event) => cmInitDots(event.target ?? document));
document.addEventListener('shopify:block:select', () => cmInitDots());

// Die Galerie tauscht sich bei Variantenwechsel komplett aus (media-gallery
// ersetzt sich selbst). Ohne diesen Beobachter blieben die neuen Punkte
// ungebunden und der Streifen haengen.
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.matches?.(CM_DOTS_SELECTOR) || node.querySelector?.(CM_DOTS_SELECTOR)) {
        cmInitDots(node.parentElement ?? document);
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });
